import sharedTransformForSubmit from '../../shared/config/submit-transformer';
import {
  isValidFullName,
  hasValidNameParts,
  handlePartialName,
} from '../utils/submissionAttemptTracking';

/**
 * Recovers a prefilled veteran name that was stripped from the submitted payload.
 *
 * The submitted name can arrive fully blank, partially blank, or complete. This
 * picks the best available name so a missing first name doesn't cause a 500:
 * - When the submitted name has no valid part, falls back to the profile name
 *   wholesale (when it has a usable part), otherwise the submitted value or {}.
 * - When the submitted name is partially valid, keeps its valid parts and
 *   backfills only the missing first/last from the profile (never the middle).
 *
 * @param {Object} transformedData - The transformed submission data (holds fullName)
 * @param {Object} form - The form state (source of view:profileFullName)
 * @returns {Object} The recovered fullName object
 */
const recoverPrefilledName = (transformedData, form) => {
  const submitted = transformedData.fullName;
  const profile = form.data?.['view:profileFullName'];
  const isValidSubmittedName = isValidFullName(submitted);
  const isPartialValidProfileName = hasValidNameParts(profile);
  const useProfile = !isValidSubmittedName && isPartialValidProfileName;
  const useSubmitted = !isValidSubmittedName && !isPartialValidProfileName;

  if (useProfile) {
    return profile;
  }

  if (useSubmitted) {
    return submitted || {};
  }

  return handlePartialName(submitted, profile);
};

const transformForSubmit = (formConfig, form) => {
  const idNumber = form.data?.idNumber;
  const dateOfBirth = form.data?.dateOfBirth;
  const transformedData = JSON.parse(
    sharedTransformForSubmit(formConfig, form),
  );

  // Recover the veteran name from prefill when it's stripped from the submitted
  // payload, to avoid a 500 on missing first name.
  // TODO: remove this flag gating once the fix is confirmed working.
  if (form.data?.['view:namePrefillFixEnabled'] === true) {
    transformedData.fullName = recoverPrefilledName(transformedData, form);
  }

  const { first, middle, last } = transformedData.fullName || {};

  if (first || last) {
    transformedData.fullName = {
      first: first?.slice(0, 12),
      last: last?.slice(0, 18),
      ...(middle && { middle: middle.charAt(0) }),
    };
  } else {
    transformedData.fullName = {};
  }
  if (!transformedData.dateOfBirth && dateOfBirth) {
    transformedData.dateOfBirth = dateOfBirth;
  }

  if (!transformedData.idNumber?.ssn && idNumber?.ssn) {
    transformedData.idNumber = idNumber;
  }

  return JSON.stringify(transformedData);
};

export default transformForSubmit;
